import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from './CommercialDashboard';
import { OUTCOME_OPTIONS } from './CommercialProspecting';
import { useIsMobile } from '../../hooks/useIsMobile';

const API = process.env.REACT_APP_BACKEND_URL;
const h = () => ({ 'Content-Type': 'application/json' });
const authOpts = (opts = {}) => ({ ...opts, credentials: 'include', headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) } });
import { fmtDate as fmt, fmtShortDate as fmtShort, fmtDateTime, fmtFull } from '../../utils/dateUtils';
const money = (v) => v ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';

const TYPE_LABELS = {
  general_contractor: 'General Contractor', custom_home_builder: 'Custom Home Builder',
  commercial_builder: 'Commercial Builder', developer: 'Developer', property_investor: 'Property Investor',
  site_contractor: 'Site Contractor', civil_contractor: 'Civil Contractor', ranch_developer: 'Ranch Developer',
  equestrian_business: 'Equestrian', property_manager: 'Property Mgmt', equipment_yard: 'Equipment Yard',
  industrial: 'Industrial', utility_contractor: 'Utility Contractor', government: 'Government', other: 'Other',
};

const ROLE_FLAGS = [
  ['is_decision_maker', 'Decision Maker'], ['is_estimator', 'Estimator'], ['is_project_manager', 'PM'],
  ['is_superintendent', 'Super'], ['is_owner', 'Owner'], ['is_accounts_payable', 'AP'],
];

const CONTACT_ROLES = ['Owner', 'President', 'Estimator', 'Project Manager', 'Superintendent', 'Purchasing', 'Procurement', 'Vendor Manager', 'Facilities Manager', 'Accounts Payable', 'Other'];

export default function CommercialAccountDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [account, setAccount] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [activities, setActivities] = useState([]);
  const [accountTasks, setAccountTasks] = useState([]);
  const [capPackages, setCapPackages] = useState([]);
  const [vendorPackets, setVendorPackets] = useState([]);
  const [vendorDocs, setVendorDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddOpp, setShowAddOpp] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showStageChange, setShowStageChange] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'call', description: '' });
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [lifecycle, setLifecycle] = useState(null);
  const [aiIntelligence, setAiIntelligence] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [bids, setBids] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acctRes, ctRes, oppRes, actRes, taskRes, capRes, vpRes, vdRes, lcRes, bidsRes] = await Promise.all([
        fetch(`${API}/api/sales/commercial/accounts/${accountId}`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/contacts`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/opportunities`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/activities`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/tasks`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/capability-packages`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/vendor-packets`, authOpts()),
        fetch(`${API}/api/sales/commercial/vendor-documents`, authOpts()),
        fetch(`${API}/api/sales/commercial/accounts/${accountId}/lifecycle`, authOpts()),
        fetch(`${API}/api/sales/commercial/bids/list?account_id=${accountId}`, authOpts()),
      ]);
      if (acctRes.ok) setAccount(await acctRes.json());
      if (ctRes.ok) setContacts(await ctRes.json());
      if (oppRes.ok) setOpps(await oppRes.json());
      if (actRes.ok) setActivities(await actRes.json());
      if (taskRes.ok) setAccountTasks(await taskRes.json());
      if (capRes.ok) setCapPackages(await capRes.json());
      if (vpRes.ok) setVendorPackets(await vpRes.json());
      if (vdRes.ok) setVendorDocs(await vdRes.json());
      if (lcRes.ok) setLifecycle(await lcRes.json());
      if (bidsRes.ok) setBids(await bidsRes.json());
    } finally { setLoading(false); }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const logActivity = async () => {
    if (!activityForm.description.trim()) return;
    setLoggingActivity(true);
    try {
      await fetch(`${API}/api/sales/commercial/accounts/${accountId}/activities`, authOpts({
        method: 'POST',
        body: JSON.stringify({ activity_type: activityForm.type, description: activityForm.description }),
      }));
      setActivityForm({ type: 'call', description: '' });
      load();
    } finally { setLoggingActivity(false); }
  };

  if (loading || !account) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading account...</div>;

  const stageColor = STAGE_COLORS[account.account_status] || '#94a3b8';
  const priColor = PRIORITY_COLORS[account.account_priority] || '#94a3b8';
  const primaryContact = contacts.find(c => c.is_primary) || contacts[0];
  const decisionMakers = contacts.filter(c => c.is_decision_maker);
  const daysSinceContact = account.last_contact_at ? Math.round((Date.now() - new Date(account.last_contact_at)) / 86400000) : null;

  const pendingTasks = accountTasks.filter(t => t.status === 'pending');
  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'contacts', label: `Contacts (${contacts.length})` },
    { key: 'opportunities', label: `Opportunities (${opps.length})` },
    { key: 'bids', label: `Bids (${bids.length})` },
    { key: 'lifecycle', label: 'Lifecycle' },
    { key: 'activities', label: 'Activities' },
    { key: 'tasks', label: `Tasks (${pendingTasks.length})` },
    { key: 'packages', label: 'Packages & Docs' },
    { key: 'notes', label: 'Notes' },
  ];

  return (
    <div data-testid="commercial-account-detail" style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '12px 10px' : '20px 28px' }}>
      {/* Back Button */}
      <button data-testid="back-to-accounts" onClick={() => navigate('/sales/portal')}
        style={{ marginBottom: 12, padding: '6px 12px', fontSize: 11, color: '#64748b', background: 'none', border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer', minHeight: 36 }}>
        &larr; Back to Sales Portal
      </button>

      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '14px 14px' : '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ width: 24, height: 24, borderRadius: '50%', background: priColor, color: '#fff', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{account.account_priority}</span>
              <h1 style={{ fontSize: isMobile ? 17 : 22, fontWeight: 800, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.company_name}</h1>
              <span style={{ padding: '3px 10px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: `${stageColor}15`, color: stageColor, border: `1px solid ${stageColor}30`, flexShrink: 0 }}>
                {STAGE_LABELS[account.account_status] || account.account_status}
              </span>
            </div>
            <div style={{ display: 'flex', gap: isMobile ? 8 : 16, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
              <span>{TYPE_LABELS[account.account_type] || account.account_type}</span>
              {account.city && <span>{account.city}, {account.state}</span>}
              {account.main_phone && <a href={`tel:${account.main_phone}`} style={{ color: '#c0392b', fontWeight: 500 }}>{account.main_phone}</a>}
              {account.main_email && <a href={`mailto:${account.main_email}`} style={{ color: '#2563eb', fontWeight: 500 }}>{account.main_email}</a>}
              {account.website && <a href={account.website.startsWith('http') ? account.website : `https://${account.website}`} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{account.website}</a>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button data-testid="change-stage-btn" onClick={() => setShowStageChange(!showStageChange)}
              style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, border: '1px solid #c0392b', borderRadius: 4, background: '#fff', color: '#c0392b', cursor: 'pointer', minHeight: 36 }}>
              Change Stage
            </button>
            <button data-testid="edit-account-btn" onClick={() => setShowEditAccount(!showEditAccount)}
              style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, border: '1px solid #475569', borderRadius: 4, background: '#fff', color: '#475569', cursor: 'pointer', minHeight: 36 }}>
              Edit
            </button>
          </div>
        </div>

        {/* Mobile Quick Actions */}
        {isMobile && (
          <div data-testid="commercial-quick-actions" style={{ display: 'flex', gap: 6, marginTop: 12, overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
            {account.main_phone && (
              <a href={`tel:${account.main_phone}`} data-testid="comm-quick-call" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#16a34a', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', minHeight: 40 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                Call
              </a>
            )}
            {account.main_email && (
              <a href={`mailto:${account.main_email}`} data-testid="comm-quick-email" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#2563eb', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', minHeight: 40 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                Email
              </a>
            )}
            <button onClick={() => setTab('activities')} data-testid="comm-quick-activity" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#f59e0b', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Log Activity
            </button>
            <button onClick={() => setTab('tasks')} data-testid="comm-quick-followup" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#8b5cf6', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
              Follow-Up
            </button>
            <button onClick={() => setShowAddContact(true)} data-testid="comm-quick-add-contact" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#0ea5e9', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
              Add Contact
            </button>
            <button onClick={() => setShowAddOpp(true)} data-testid="comm-quick-add-opp" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: '#c0392b', color: '#fff', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 40 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Create Opp
            </button>
          </div>
        )}

        {/* Stage Change Panel */}
        {showStageChange && <StageChangePanel accountId={accountId} currentStage={account.account_status}
          onChanged={() => { setShowStageChange(false); load(); }} />}

        {/* KPI Row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)', gap: isMobile ? 8 : 12, marginTop: 16 }}>
          <HeaderKPI label="Primary Contact" value={primaryContact ? `${primaryContact.first_name} ${primaryContact.last_name}` : 'None'} />
          <HeaderKPI label="Decision Makers" value={decisionMakers.length} />
          <HeaderKPI label="Last Contact" value={daysSinceContact !== null ? `${daysSinceContact}d ago` : 'Never'} warn={daysSinceContact === null || daysSinceContact > 14} />
          <HeaderKPI label="Lifetime Quoted" value={money(account.lifetime_quoted)} />
          <HeaderKPI label="Lifetime Won" value={money(account.lifetime_won)} color="#16a34a" />
          <HeaderKPI label="Open Pipeline" value={money(account.open_pipeline)} color="#3b82f6" />
        </div>
      </div>

      {/* Edit Account Inline */}
      {showEditAccount && <EditAccountPanel account={account} onSaved={() => { setShowEditAccount(false); load(); }} />}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
            style={{
              padding: isMobile ? '8px 12px' : '8px 18px', fontSize: isMobile ? 11 : 12, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'none',
              borderBottom: tab === t.key ? '2px solid #c0392b' : '2px solid transparent',
              color: tab === t.key ? '#c0392b' : '#64748b', marginBottom: -2, whiteSpace: 'nowrap', minHeight: 36,
            }}>{t.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 16 }}>
          <div>
            {/* Recent Activity */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px', marginBottom: 12 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Recent Activity</h3>
              {activities.slice(0, 8).map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                  <span style={{ color: '#94a3b8', fontSize: 10, minWidth: 60 }}>{fmtShort(a.created_at)}</span>
                  <span style={{ padding: '1px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', fontSize: 10, fontWeight: 600 }}>{a.type}</span>
                  <span style={{ color: '#475569', flex: 1 }}>{a.description}</span>
                </div>
              ))}
              {activities.length === 0 && <div style={{ color: '#94a3b8', fontSize: 12 }}>No activities yet. Log your first contact.</div>}
            </div>

            {/* Current Opportunities */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>Opportunities</h3>
                <button data-testid="add-opportunity-btn" onClick={() => setShowAddOpp(true)}
                  style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  + New Opportunity
                </button>
              </div>
              {opps.map(o => (
                <div key={o.id} onClick={() => navigate(`/sales/opportunity/${o.id}`)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{o.title}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{o.current_stage?.replace(/_/g, ' ')} | {fmtShort(o.created_at)}</div>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{money(o.estimated_value || o.quoted_value)}</span>
                </div>
              ))}
              {opps.length === 0 && <div style={{ color: '#94a3b8', fontSize: 12 }}>No opportunities yet.</div>}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Log Contact Outcome */}
            <ContactOutcomePanel accountId={accountId} contacts={contacts} onLogged={load} />

            {/* Quick Log Activity */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Log Activity</h3>
              <select data-testid="activity-type" value={activityForm.type} onChange={e => setActivityForm(f => ({ ...f, type: e.target.value }))}
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, marginBottom: 6 }}>
                <option value="call">Call</option><option value="email">Email</option><option value="meeting">Meeting</option>
                <option value="site_visit">Site Visit</option><option value="note">Note</option>
              </select>
              <textarea data-testid="activity-desc" value={activityForm.description} onChange={e => setActivityForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What happened?" rows={2} style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', marginBottom: 6 }} />
              <button data-testid="log-activity-btn" onClick={logActivity} disabled={!activityForm.description.trim() || loggingActivity}
                style={{ width: '100%', padding: '7px 0', background: activityForm.description.trim() ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: activityForm.description.trim() ? 'pointer' : 'default' }}>
                {loggingActivity ? 'Logging...' : 'Log Activity'}
              </button>
            </div>

            {/* Key Contacts */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: 0 }}>Contacts</h3>
                <button data-testid="add-contact-btn" onClick={() => setShowAddContact(true)}
                  style={{ padding: '2px 8px', fontSize: 9, fontWeight: 700, background: '#1e293b', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>+</button>
              </div>
              {contacts.map(c => (
                <div key={c.id} style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{c.first_name} {c.last_name}</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                    <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#f1f5f9', color: '#475569' }}>{c.role}</span>
                    {c.is_decision_maker && <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#fef2f2', color: '#dc2626', fontWeight: 700 }}>DM</span>}
                    {c.is_primary && <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#eff6ff', color: '#2563eb', fontWeight: 700 }}>Primary</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                    {c.phone && <a href={`tel:${c.phone}`} style={{ fontSize: 10, color: '#c0392b' }}>{c.phone}</a>}
                    {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 10, color: '#2563eb' }}>{c.email}</a>}
                  </div>
                </div>
              ))}
              {contacts.length === 0 && <div style={{ color: '#94a3b8', fontSize: 11 }}>No contacts. Add the first contact.</div>}
            </div>

            {/* Stage History */}
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 16px' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Stage History</h3>
              {(account.stage_history || []).slice().reverse().map((sh, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f8fafc' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: STAGE_COLORS[sh.stage] || '#94a3b8', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#1e293b' }}>{STAGE_LABELS[sh.stage] || sh.stage}</span>
                    <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 6 }}>{fmtShort(sh.entered_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contacts Tab */}
      {tab === 'contacts' && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '12px 12px' : '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>All Contacts</h3>
            <button data-testid="add-contact-tab-btn" onClick={() => setShowAddContact(true)}
              style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', minHeight: 36 }}>+ Add Contact</button>
          </div>
          {contacts.map(c => (
            <div key={c.id} style={{ display: isMobile ? 'flex' : 'grid', gridTemplateColumns: isMobile ? undefined : '1fr 1fr 1fr 1fr', flexDirection: isMobile ? 'column' : undefined, gap: isMobile ? 4 : 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9', alignItems: isMobile ? 'stretch' : 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{c.first_name} {c.last_name}</div>
                <div style={{ fontSize: 11, color: '#64748b' }}>{c.title}</div>
              </div>
              {!isMobile && <div style={{ fontSize: 11, color: '#475569' }}>{c.role}</div>}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {isMobile && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 2, background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>{c.role}</span>}
                {ROLE_FLAGS.filter(([k]) => c[k]).map(([k, label]) => (
                  <span key={k} style={{ fontSize: 9, padding: '1px 5px', borderRadius: 2, background: k === 'is_decision_maker' ? '#fef2f2' : '#f1f5f9', color: k === 'is_decision_maker' ? '#dc2626' : '#475569', fontWeight: 600 }}>{label}</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {c.phone && <a href={`tel:${c.phone}`} style={{ fontSize: 11, color: '#c0392b', fontWeight: 500 }}>{c.phone}</a>}
                {c.email && <a href={`mailto:${c.email}`} style={{ fontSize: 11, color: '#2563eb', fontWeight: 500 }}>{c.email}</a>}
              </div>
            </div>
          ))}
          {contacts.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No contacts yet.</div>}
        </div>
      )}

      {/* Opportunities Tab */}
      {tab === 'opportunities' && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '12px 12px' : '16px 18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>Commercial Opportunities</h3>
            <button data-testid="add-opp-tab-btn" onClick={() => setShowAddOpp(true)}
              style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', minHeight: 36 }}>+ New Opportunity</button>
          </div>
          {opps.map(o => (
            <div key={o.id} onClick={() => navigate(`/sales/opportunity/${o.id}`)}
              style={{ display: isMobile ? 'flex' : 'grid', gridTemplateColumns: isMobile ? undefined : '2fr 1fr 1fr 1fr', flexDirection: isMobile ? 'column' : undefined, gap: isMobile ? 4 : 10, padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', alignItems: isMobile ? 'stretch' : 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{o.title}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{(o.services || []).join(', ')}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#f1f5f9', color: '#475569', fontWeight: 600 }}>{o.current_stage?.replace(/_/g, ' ')}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{money(o.estimated_value)}</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{fmtShort(o.created_at)}</span>
              </div>
            </div>
          ))}
          {opps.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No opportunities yet.</div>}
        </div>
      )}

      {/* Activities Tab */}
      {tab === 'activities' && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Activity Timeline</h3>
          {activities.map((a, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ minWidth: 80, fontSize: 10, color: '#94a3b8' }}>{fmt(a.created_at)}</div>
              <span style={{ padding: '2px 6px', borderRadius: 3, background: '#f1f5f9', color: '#475569', fontSize: 10, fontWeight: 600, height: 'fit-content' }}>{a.type}</span>
              <div style={{ flex: 1, fontSize: 12, color: '#475569' }}>{a.description}</div>
            </div>
          ))}
          {activities.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No activities logged yet.</div>}
        </div>
      )}

      {/* Notes Tab */}
      {tab === 'notes' && (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px' }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Account Notes</h3>
          <p style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap' }}>{account.notes || 'No notes yet.'}</p>
        </div>
      )}

      {/* Tasks Tab */}
      {tab === 'tasks' && (
        <TasksTab accountId={accountId} tasks={accountTasks} onRefresh={load} />
      )}

      {/* Packages & Docs Tab */}
      {tab === 'packages' && (
        <PackagesTab accountId={accountId} contacts={contacts} capPackages={capPackages}
          vendorPackets={vendorPackets} vendorDocs={vendorDocs} onRefresh={load} />
      )}

      {/* Bids Tab */}
      {tab === 'bids' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>Bids</h3>
            <button onClick={() => navigate('/sales/portal')} style={{ padding: '8px 14px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
              Manage Bids
            </button>
          </div>
          {bids.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>No bids for this account.</div>
          ) : bids.map(b => (
            <div key={b.id} onClick={() => navigate(`/sales/bids/${b.id}`)} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', marginBottom: 8, cursor: 'pointer', borderLeft: '3px solid ' + ({invited:'#3b82f6',awarded:'#16a34a',lost:'#6b7280',bid_submitted:'#14b8a6'}[b.bid_status]||'#94a3b8') }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{b.project_name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: {invited:'#3b82f6',awarded:'#16a34a',lost:'#6b7280'}[b.bid_status]||'#475569' }}>{b.bid_status}</span>
              </div>
              {b.bid_due_date && <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>Due: {fmt(b.bid_due_date)}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Lifecycle Tab */}
      {tab === 'lifecycle' && (
        <LifecycleTab accountId={accountId} lifecycle={lifecycle} aiIntelligence={aiIntelligence}
          loadingAI={loadingAI} account={account} onRefresh={load}
          onLoadAI={async () => {
            setLoadingAI(true);
            try {
              const res = await fetch(`${API}/api/sales/commercial/accounts/${accountId}/ai-intelligence`, authOpts());
              if (res.ok) setAiIntelligence(await res.json());
            } finally { setLoadingAI(false); }
          }}
          isMobile={isMobile} />
      )}

      {/* Add Contact Modal */}
      {showAddContact && <AddContactModal accountId={accountId} onClose={() => setShowAddContact(false)} onCreated={() => { setShowAddContact(false); load(); }} />}

      {/* Add Opportunity Modal */}
      {showAddOpp && <AddOppModal accountId={accountId} contacts={contacts} onClose={() => setShowAddOpp(false)} onCreated={(id) => { setShowAddOpp(false); load(); if (id) navigate(`/sales/opportunity/${id}`); }} />}
    </div>
  );
}


function HeaderKPI({ label, value, color, warn }) {
  return (
    <div style={{ textAlign: 'center', padding: '6px 0' }}>
      <div style={{ fontSize: 9, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: warn ? '#dc2626' : color || '#1e293b', marginTop: 2 }}>{value}</div>
    </div>
  );
}


function StageChangePanel({ accountId, currentStage, onChanged }) {
  const [stage, setStage] = useState(currentStage);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts/${accountId}/stage`, authOpts({
        method: 'POST', body: JSON.stringify({ stage, reason }),
      }));
      if (res.ok) onChanged();
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="stage-change-panel" style={{ marginTop: 12, padding: '12px 14px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select data-testid="new-stage-select" value={stage} onChange={e => setStage(e.target.value)}
          style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11 }}>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input data-testid="stage-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)"
          style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11 }} />
        <button data-testid="confirm-stage-btn" onClick={save} disabled={stage === currentStage || saving}
          style={{ padding: '6px 16px', background: stage !== currentStage ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: stage !== currentStage ? 'pointer' : 'default' }}>
          {saving ? 'Saving...' : 'Update Stage'}
        </button>
      </div>
    </div>
  );
}


function EditAccountPanel({ account, onSaved }) {
  const [form, setForm] = useState({
    company_name: account.company_name || '', account_type: account.account_type || 'other',
    account_priority: account.account_priority || 'C', main_phone: account.main_phone || '',
    main_email: account.main_email || '', website: account.website || '', notes: account.notes || '',
    city: account.city || '', state: account.state || '', zip: account.zip || '',
    physical_address: account.physical_address || '', next_action: account.next_action || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts/${account.id}`, authOpts({
        method: 'PUT', body: JSON.stringify(form),
      }));
      if (res.ok) onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="edit-account-panel" style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px', marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Company</label><input value={form.company_name} onChange={e => set('company_name', e.target.value)} style={fieldStyle} /></div>
        <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Phone</label><input value={form.main_phone} onChange={e => set('main_phone', e.target.value)} style={fieldStyle} /></div>
        <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Email</label><input value={form.main_email} onChange={e => set('main_email', e.target.value)} style={fieldStyle} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
        <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Priority</label>
          <select value={form.account_priority} onChange={e => set('account_priority', e.target.value)} style={fieldStyle}>
            <option value="A">A — Dream</option><option value="B">B — Strong</option><option value="C">C — Prospect</option>
          </select>
        </div>
        <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Next Action</label><input value={form.next_action} onChange={e => set('next_action', e.target.value)} style={fieldStyle} placeholder="e.g. Follow up on vendor app" /></div>
      </div>
      <div><label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...fieldStyle, resize: 'vertical' }} /></div>
      <button onClick={save} disabled={saving} style={{ marginTop: 8, padding: '7px 20px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
}


function AddContactModal({ accountId, onClose, onCreated }) {
  const [form, setForm] = useState({
    account_id: accountId, first_name: '', last_name: '', title: '', role: 'Other',
    email: '', phone: '', mobile: '', is_primary: false, is_decision_maker: false,
    is_estimator: false, is_project_manager: false, is_owner: false, notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/contacts`, authOpts({
        method: 'POST', body: JSON.stringify(form),
      }));
      if (res.ok) onCreated();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div data-testid="add-contact-modal" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, padding: '20px 20px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: 0 }}>Add Contact</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>First Name</label><input data-testid="contact-first" value={form.first_name} onChange={e => set('first_name', e.target.value)} style={fieldStyle} /></div>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Last Name</label><input data-testid="contact-last" value={form.last_name} onChange={e => set('last_name', e.target.value)} style={fieldStyle} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Title</label><input value={form.title} onChange={e => set('title', e.target.value)} style={fieldStyle} /></div>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Role</label>
            <select data-testid="contact-role" value={form.role} onChange={e => set('role', e.target.value)} style={fieldStyle}>
              {CONTACT_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Phone</label><input data-testid="contact-phone" value={form.phone} onChange={e => set('phone', e.target.value)} style={fieldStyle} /></div>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Email</label><input data-testid="contact-email" value={form.email} onChange={e => set('email', e.target.value)} style={fieldStyle} /></div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {[['is_primary', 'Primary Contact'], ['is_decision_maker', 'Decision Maker'], ['is_estimator', 'Estimator'], ['is_project_manager', 'Project Manager'], ['is_owner', 'Owner']].map(([k, label]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={form[k]} onChange={e => set(k, e.target.checked)} style={{ accentColor: '#c0392b' }} />{label}
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, background: '#fff', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="save-contact-btn" onClick={save} disabled={!form.first_name.trim() || saving}
            style={{ padding: '7px 20px', background: form.first_name.trim() ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: form.first_name.trim() ? 'pointer' : 'default' }}>
            {saving ? 'Saving...' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}


function AddOppModal({ accountId, contacts, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', services: [], estimated_value: '', priority: 'medium',
    commercial_contact_id: contacts[0]?.id || '', project_name: '', project_address: '',
    bid_due_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };
  const SERVICES = ['Pipe Fence', 'Chain Link Fence', 'Privacy Fence', 'Iron Fence', 'Gate / Automation', 'Welding / Fabrication', 'Concrete', 'Land Clearing', 'Custom Metal Work', 'Fence Repair', 'Carport / Barn'];

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts/${accountId}/opportunities`, authOpts({
        method: 'POST',
        body: JSON.stringify({ ...form, estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null }),
      }));
      if (res.ok) { const data = await res.json(); onCreated(data.id); }
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}>
      <div data-testid="add-opp-modal" style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, padding: '20px 20px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: '#1e293b', margin: 0 }}>New Commercial Opportunity</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Title / Project Name</label><input data-testid="opp-title" value={form.title} onChange={e => set('title', e.target.value)} style={fieldStyle} placeholder="e.g. Downtown Office Perimeter Fence" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Estimated Value</label><input type="number" value={form.estimated_value} onChange={e => set('estimated_value', e.target.value)} style={fieldStyle} /></div>
            <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Bid Due Date</label><input type="date" value={form.bid_due_date} onChange={e => set('bid_due_date', e.target.value)} style={fieldStyle} /></div>
          </div>
          {contacts.length > 0 && <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Contact</label>
            <select value={form.commercial_contact_id} onChange={e => set('commercial_contact_id', e.target.value)} style={fieldStyle}>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.role}</option>)}
            </select>
          </div>}
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Services</label>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {SERVICES.map(s => (
                <button key={s} onClick={() => set('services', form.services.includes(s) ? form.services.filter(x => x !== s) : [...form.services, s])}
                  style={{ padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer', border: '1px solid',
                    background: form.services.includes(s) ? '#c0392b' : '#fff', color: form.services.includes(s) ? '#fff' : '#64748b',
                    borderColor: form.services.includes(s) ? '#c0392b' : '#d1d5db' }}>{s}</button>
              ))}
            </div>
          </div>
          <div><label style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>Notes</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} style={{ ...fieldStyle, resize: 'vertical' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button onClick={onClose} style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, background: '#fff', color: '#64748b', cursor: 'pointer' }}>Cancel</button>
          <button data-testid="save-opp-btn" onClick={save} disabled={!form.title.trim() || saving}
            style={{ padding: '7px 20px', background: form.title.trim() ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: form.title.trim() ? 'pointer' : 'default' }}>
            {saving ? 'Creating...' : 'Create Opportunity'}
          </button>
        </div>
      </div>
    </div>
  );
}


function ContactOutcomePanel({ accountId, contacts, onLogged }) {
  const [outcome, setOutcome] = useState('');
  const [contactId, setContactId] = useState('');
  const [notes, setNotes] = useState('');
  const [method, setMethod] = useState('call');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!outcome) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/sales/commercial/accounts/${accountId}/contact-outcome`, authOpts({
        method: 'POST',
        body: JSON.stringify({ outcome, contact_id: contactId || null, notes, contact_method: method }),
      }));
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setOutcome('');
        setNotes('');
        setContactId('');
        setTimeout(() => { setResult(null); onLogged(); }, 2000);
      }
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="contact-outcome-panel" style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 16px', marginBottom: 12 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Log Contact Outcome</h3>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {['call', 'email'].map(m => (
          <button key={m} onClick={() => setMethod(m)} data-testid={`method-${m}`}
            style={{ flex: 1, padding: '4px 0', fontSize: 10, fontWeight: 600, borderRadius: 3, cursor: 'pointer',
              background: method === m ? '#c0392b' : '#fff', color: method === m ? '#fff' : '#64748b',
              border: `1px solid ${method === m ? '#c0392b' : '#d1d5db'}` }}>
            {m.charAt(0).toUpperCase() + m.slice(1)}
          </button>
        ))}
      </div>
      {contacts.length > 0 && (
        <select data-testid="outcome-contact-select" value={contactId} onChange={e => setContactId(e.target.value)}
          style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, marginBottom: 6 }}>
          <option value="">Select contact (optional)</option>
          {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.role}</option>)}
        </select>
      )}
      <select data-testid="outcome-type-select" value={outcome} onChange={e => setOutcome(e.target.value)}
        style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, marginBottom: 6 }}>
        <option value="">Select outcome...</option>
        {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <textarea data-testid="outcome-notes-input" value={notes} onChange={e => setNotes(e.target.value)}
        placeholder="Notes (optional)" rows={2}
        style={{ width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, resize: 'vertical', boxSizing: 'border-box', marginBottom: 6 }} />
      <button data-testid="submit-outcome-btn" onClick={submit} disabled={!outcome || saving}
        style={{ width: '100%', padding: '7px 0', background: outcome ? '#c0392b' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: outcome ? 'pointer' : 'default' }}>
        {saving ? 'Logging...' : 'Log Outcome'}
      </button>
      {result && (
        <div style={{ marginTop: 6, padding: '6px 8px', borderRadius: 4, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 10 }}>
          <span style={{ fontWeight: 600, color: '#16a34a' }}>Logged.</span>
          {result.stage_updated && <span style={{ color: '#475569' }}> Stage → {STAGE_LABELS[result.stage_updated] || result.stage_updated}</span>}
          {result.auto_task && <span style={{ color: '#475569' }}> + Auto-task created</span>}
        </div>
      )}
    </div>
  );
}


function TasksTab({ accountId, tasks, onRefresh }) {
  const [form, setForm] = useState({ description: '', due_days: 1, priority: 'medium' });
  const [saving, setSaving] = useState(false);
  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const completedTasks = tasks.filter(t => t.status === 'completed');

  const createTask = async () => {
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/api/sales/commercial/tasks`, authOpts({
        method: 'POST',
        body: JSON.stringify({ account_id: accountId, ...form }),
      }));
      setForm({ description: '', due_days: 1, priority: 'medium' });
      onRefresh();
    } finally { setSaving(false); }
  };

  const completeTask = async (taskId) => {
    await fetch(`${API}/api/sales/commercial/tasks/${taskId}/complete`, authOpts({
      method: 'POST', body: JSON.stringify({ result: 'Completed' }),
    }));
    onRefresh();
  };

  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

  return (
    <div data-testid="tasks-tab" style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px' }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Account Tasks</h3>

      {/* Create Task */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <input data-testid="new-task-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="New task..." style={fieldStyle} />
        </div>
        <select value={form.due_days} onChange={e => setForm(f => ({ ...f, due_days: parseInt(e.target.value) }))}
          style={{ ...fieldStyle, width: 'auto' }}>
          <option value={1}>1 day</option><option value={2}>2 days</option><option value={3}>3 days</option>
          <option value={5}>5 days</option><option value={7}>1 week</option>
        </select>
        <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
          style={{ ...fieldStyle, width: 'auto' }}>
          <option value="low">Low</option><option value="medium">Med</option><option value="high">High</option>
        </select>
        <button data-testid="create-task-btn" onClick={createTask} disabled={!form.description.trim() || saving}
          style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {saving ? '...' : '+ Task'}
        </button>
      </div>

      {/* Pending Tasks */}
      {pendingTasks.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pending ({pendingTasks.length})</div>
          {pendingTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{t.description}</div>
                <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                  <span>Due: {t.due_at?.slice(0, 10)}</span>
                  <span style={{ padding: '0 4px', borderRadius: 2, background: t.priority === 'high' ? '#fef2f2' : '#f1f5f9', color: t.priority === 'high' ? '#dc2626' : '#64748b', fontWeight: 600 }}>{t.priority}</span>
                  <span style={{ color: '#94a3b8' }}>{t.task_type?.replace('commercial_', '')}</span>
                </div>
              </div>
              <button data-testid={`complete-task-${t.id}`} onClick={() => completeTask(t.id)}
                style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' }}>
                Done
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Completed Tasks */}
      {completedTasks.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Completed ({completedTasks.length})</div>
          {completedTasks.slice(0, 10).map(t => (
            <div key={t.id} style={{ padding: '6px 0', borderBottom: '1px solid #f8fafc' }}>
              <div style={{ fontSize: 12, color: '#94a3b8', textDecoration: 'line-through' }}>{t.description}</div>
              <div style={{ fontSize: 10, color: '#d1d5db' }}>Completed {fmtShort(t.completed_at)}</div>
            </div>
          ))}
        </div>
      )}

      {tasks.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No tasks. Create one or log an outcome to auto-generate.</div>}
    </div>
  );
}


function PackagesTab({ accountId, contacts, capPackages, vendorPackets, vendorDocs, onRefresh }) {
  const [capForm, setCapForm] = useState({ contact_id: contacts[0]?.id || '', custom_message: '' });
  const [vpForm, setVpForm] = useState({ contact_id: contacts[0]?.id || '', document_ids: [] });
  const [savingCap, setSavingCap] = useState(false);
  const [savingVp, setSavingVp] = useState(false);
  const fieldStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };

  const VENDOR_DOC_TYPES = ['W-9', 'Certificate of Insurance (COI)', 'Capability Statement', 'References', 'Safety Program', 'Remittance / Payment Info', 'Other'];

  const sendCapability = async () => {
    setSavingCap(true);
    try {
      await fetch(`${API}/api/sales/commercial/accounts/${accountId}/capability-package`, authOpts({
        method: 'POST',
        body: JSON.stringify({ contact_id: capForm.contact_id || null, custom_message: capForm.custom_message }),
      }));
      setCapForm({ contact_id: contacts[0]?.id || '', custom_message: '' });
      onRefresh();
    } finally { setSavingCap(false); }
  };

  const sendVendorPacket = async () => {
    if (vpForm.document_ids.length === 0) return;
    setSavingVp(true);
    try {
      await fetch(`${API}/api/sales/commercial/accounts/${accountId}/vendor-packet`, authOpts({
        method: 'POST',
        body: JSON.stringify({ contact_id: vpForm.contact_id || null, document_ids: vpForm.document_ids }),
      }));
      setVpForm({ contact_id: contacts[0]?.id || '', document_ids: [] });
      onRefresh();
    } finally { setSavingVp(false); }
  };

  const toggleDoc = (docId) => {
    setVpForm(f => ({
      ...f,
      document_ids: f.document_ids.includes(docId) ? f.document_ids.filter(d => d !== docId) : [...f.document_ids, docId],
    }));
  };

  const isExpiring = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const days = (d - Date.now()) / 86400000;
    return days >= 0 && days <= 30;
  };

  return (
    <div data-testid="packages-tab">
      {/* Send Capability Package */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Send Capability Package</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Recipient Contact</label>
            <select data-testid="cap-contact" value={capForm.contact_id} onChange={e => setCapForm(f => ({ ...f, contact_id: e.target.value }))} style={fieldStyle}>
              <option value="">Use account email</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.email || 'no email'}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Custom Message (optional)</label>
            <input data-testid="cap-message" value={capForm.custom_message} onChange={e => setCapForm(f => ({ ...f, custom_message: e.target.value }))} style={fieldStyle} placeholder="Personalized intro..." />
          </div>
        </div>
        <button data-testid="send-cap-btn" onClick={sendCapability} disabled={savingCap}
          style={{ padding: '7px 20px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
          {savingCap ? 'Sending...' : 'Record Capability Package Send'}
        </button>

        {/* History */}
        {capPackages.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Sent History</div>
            {capPackages.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f8fafc', fontSize: 11 }}>
                <span><span style={{ fontWeight: 600 }}>{p.sent_to_name}</span> — {p.sent_to}</span>
                <span style={{ color: '#94a3b8' }}>{fmtShort(p.sent_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Vendor Packet */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '16px 18px', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Send Vendor Packet</h3>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Recipient</label>
          <select data-testid="vp-contact" value={vpForm.contact_id} onChange={e => setVpForm(f => ({ ...f, contact_id: e.target.value }))} style={fieldStyle}>
            <option value="">Use account email</option>
            {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.email || 'no email'}</option>)}
          </select>
        </div>

        {/* Available Documents */}
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Select Documents to Include</label>
          {vendorDocs.length === 0 && (
            <div style={{ padding: '8px 0', fontSize: 11, color: '#94a3b8' }}>
              No vendor documents uploaded. Admin must add W-9, COI, etc. via settings.
            </div>
          )}
          {vendorDocs.map(d => (
            <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 11, color: '#475569', cursor: 'pointer' }}>
              <input type="checkbox" checked={vpForm.document_ids.includes(d.id)} onChange={() => toggleDoc(d.id)} style={{ accentColor: '#c0392b' }} />
              <span style={{ fontWeight: 600 }}>{d.document_type || d.name}</span>
              {d.name && <span style={{ color: '#94a3b8' }}>({d.name})</span>}
              {d.version && <span style={{ fontSize: 9, color: '#94a3b8' }}>v{d.version}</span>}
              {isExpiring(d.expiration_date) && <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#fef2f2', color: '#dc2626', fontWeight: 700 }}>Expiring Soon</span>}
              {d.expiration_date && new Date(d.expiration_date) < new Date() && <span style={{ fontSize: 9, padding: '1px 4px', borderRadius: 2, background: '#dc2626', color: '#fff', fontWeight: 700 }}>EXPIRED</span>}
            </label>
          ))}
        </div>

        <button data-testid="send-vp-btn" onClick={sendVendorPacket} disabled={vpForm.document_ids.length === 0 || savingVp}
          style={{ padding: '7px 20px', background: vpForm.document_ids.length > 0 ? '#ec4899' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: vpForm.document_ids.length > 0 ? 'pointer' : 'default' }}>
          {savingVp ? 'Sending...' : `Record Vendor Packet Send (${vpForm.document_ids.length} doc${vpForm.document_ids.length !== 1 ? 's' : ''})`}
        </button>

        {/* History */}
        {vendorPackets.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>Vendor Packet History</div>
            {vendorPackets.map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f8fafc', fontSize: 11 }}>
                <span><span style={{ fontWeight: 600 }}>{p.sent_to_name}</span> — {p.document_ids?.length || 0} docs</span>
                <span style={{ color: '#94a3b8' }}>{fmtShort(p.sent_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vendor Document Types Reference */}
      <div style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', padding: '14px 18px' }}>
        <h4 style={{ fontSize: 12, fontWeight: 700, color: '#64748b', margin: '0 0 6px' }}>Standard Vendor Documents</h4>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {VENDOR_DOC_TYPES.map(t => (
            <span key={t} style={{ padding: '3px 8px', fontSize: 10, borderRadius: 3, background: '#fff', border: '1px solid #e2e8f0', color: '#475569' }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}



function LifecycleTab({ accountId, lifecycle, aiIntelligence, loadingAI, account, onRefresh, onLoadAI, isMobile }) {
  const HEALTH_COLORS = { strong: '#16a34a', healthy: '#22c55e', at_risk: '#f59e0b', needs_attention: '#f97316', critical: '#ef4444' };

  const applyRecommendation = async (stage, notes) => {
    await fetch(`${API}/api/sales/commercial/accounts/${accountId}/lifecycle/apply`, authOpts({
      method: 'POST', body: JSON.stringify({ stage, notes }),
    }));
    onRefresh();
  };

  const confirmRepeat = async () => {
    await fetch(`${API}/api/sales/commercial/accounts/${accountId}/confirm-repeat`, authOpts({ method: 'POST' }));
    onRefresh();
  };

  const cardS = { background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '16px 20px', marginBottom: 12 };
  const lbl = { fontSize: 11, color: '#94a3b8', margin: '0 0 2px', textTransform: 'uppercase' };
  const val = { fontSize: 13, color: '#1e293b', margin: 0, fontWeight: 500 };

  return (
    <div data-testid="lifecycle-tab">
      {/* Lifecycle Detection */}
      <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>Account Lifecycle</h3>
      {lifecycle ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={cardS}>
            <p style={lbl}>First Job</p>
            <p style={{ ...val, color: lifecycle.first_job?.has_first_job ? '#16a34a' : '#94a3b8', fontSize: 15, fontWeight: 700 }}>
              {lifecycle.first_job?.has_first_job ? `Yes (${lifecycle.first_job.total_awarded_projects} project${lifecycle.first_job.total_awarded_projects !== 1 ? 's' : ''})` : 'No awarded projects yet'}
            </p>
          </div>
          <div style={cardS}>
            <p style={lbl}>Repeat Status</p>
            <p style={{ ...val, color: lifecycle.repeat?.qualifies ? '#16a34a' : '#94a3b8', fontSize: 15, fontWeight: 700 }}>
              {lifecycle.repeat?.qualifies ? `Qualifies (${lifecycle.repeat.distinct_projects} projects)` : `Not yet (${lifecycle.repeat?.distinct_projects || 0} projects)`}
            </p>
            {!lifecycle.repeat?.qualifies && !account?.repeat_confirmed_by_human && (
              <button data-testid="confirm-repeat-btn" onClick={confirmRepeat}
                style={{ marginTop: 8, padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, color: '#475569', cursor: 'pointer', minHeight: 32 }}>
                Confirm Recurring Vendor
              </button>
            )}
          </div>
          <div style={cardS}>
            <p style={lbl}>Dormant Check</p>
            <p style={{ ...val, color: lifecycle.dormant?.is_dormant ? '#ef4444' : '#16a34a', fontSize: 15, fontWeight: 700 }}>
              {lifecycle.dormant?.is_dormant ? `Dormant (${lifecycle.dormant.days_since_contact || '?'}d no contact)` : 'Active'}
            </p>
            {lifecycle.dormant?.days_since_contact != null && (
              <p style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                Last contact: {lifecycle.dormant.days_since_contact}d ago (threshold: {lifecycle.dormant.threshold_days}d)
              </p>
            )}
          </div>
        </div>
      ) : (
        <p style={{ color: '#94a3b8', fontSize: 13 }}>Loading lifecycle data...</p>
      )}

      {/* Recommendations */}
      {lifecycle?.recommendations?.length > 0 && (
        <div style={{ ...cardS, borderColor: '#f59e0b', background: '#fffbeb' }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: '#92400e', margin: '0 0 10px' }}>Lifecycle Recommendations</h4>
          {lifecycle.recommendations.map((rec, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 8, padding: '8px 0', borderTop: i > 0 ? '1px solid #fde68a' : 'none' }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>Move to: {rec.suggested_stage}</span>
                <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>{rec.reason}</p>
                <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: rec.confidence === 'CONFIRMED' ? '#dcfce7' : '#fef3c7', color: rec.confidence === 'CONFIRMED' ? '#166534' : '#92400e', fontWeight: 600 }}>
                  {rec.confidence}
                </span>
              </div>
              <button data-testid={`apply-rec-${rec.suggested_stage}`} onClick={() => applyRecommendation(rec.suggested_stage, rec.reason)}
                style={{ padding: '8px 16px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 36, whiteSpace: 'nowrap' }}>
                Apply
              </button>
            </div>
          ))}
        </div>
      )}

      {/* AI Intelligence */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>AI Account Intelligence</h3>
        <button data-testid="load-ai-intel-btn" onClick={onLoadAI} disabled={loadingAI}
          style={{ padding: '8px 16px', background: loadingAI ? '#94a3b8' : '#6366f1', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 36 }}>
          {loadingAI ? 'Analyzing...' : aiIntelligence ? 'Refresh Intelligence' : 'Generate Intelligence'}
        </button>
      </div>
      <div style={{ background: '#eef2ff', borderRadius: 8, border: '1px solid #c7d2fe', padding: '10px 14px', marginBottom: 12 }}>
        <p style={{ fontSize: 11, color: '#4338ca', margin: 0 }}>AI can recommend actions but cannot autonomously change account status. All suggestions require human approval.</p>
      </div>

      {aiIntelligence?.intelligence && !aiIntelligence.intelligence.error ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
          {/* Health Card */}
          <div style={cardS}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: HEALTH_COLORS[aiIntelligence.intelligence.account_health] || '#94a3b8' }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', textTransform: 'capitalize' }}>
                {(aiIntelligence.intelligence.account_health || '').replace(/_/g, ' ')}
              </span>
            </div>
            {aiIntelligence.intelligence.health_reasons?.map((r, i) => (
              <p key={i} style={{ fontSize: 12, color: '#475569', margin: '4px 0' }}>• {r}</p>
            ))}
            {aiIntelligence.intelligence.relationship_insights && (
              <p style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>{aiIntelligence.intelligence.relationship_insights}</p>
            )}
          </div>

          {/* Next Best Actions */}
          <div style={cardS}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Next Best Actions</h4>
            {(aiIntelligence.intelligence.next_best_actions || []).map((a, i) => (
              <div key={i} style={{ padding: '8px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: a.priority === 'high' ? '#fef2f2' : a.priority === 'medium' ? '#fef3c7' : '#f1f5f9', color: a.priority === 'high' ? '#991b1b' : a.priority === 'medium' ? '#92400e' : '#475569', fontWeight: 600 }}>
                    {a.priority?.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{a.action}</span>
                </div>
                <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0 0' }}>{a.reason}</p>
                {a.timeframe && <span style={{ fontSize: 10, color: '#94a3b8' }}>{a.timeframe}</span>}
              </div>
            ))}
          </div>

          {/* Expansion Opportunities */}
          {aiIntelligence.intelligence.expansion_opportunities?.length > 0 && (
            <div style={cardS}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' }}>Expansion Opportunities</h4>
              {aiIntelligence.intelligence.expansion_opportunities.map((o, i) => (
                <div key={i} style={{ padding: '8px 0', borderTop: i > 0 ? '1px solid #f1f5f9' : 'none' }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', margin: 0 }}>{o.opportunity}</p>
                  {o.potential_services?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                      {o.potential_services.map((s, j) => <span key={j} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: '#f0fdf4', color: '#166534' }}>{s}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Risk Factors */}
          {aiIntelligence.intelligence.risk_factors?.length > 0 && (
            <div style={{ ...cardS, borderColor: '#fecaca' }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', margin: '0 0 8px' }}>Risk Factors</h4>
              {aiIntelligence.intelligence.risk_factors.map((r, i) => (
                <p key={i} style={{ fontSize: 12, color: '#991b1b', margin: '4px 0' }}>• {r}</p>
              ))}
            </div>
          )}
        </div>
      ) : aiIntelligence?.intelligence?.error ? (
        <div style={cardS}><p style={{ color: '#ef4444', fontSize: 13 }}>AI Error: {aiIntelligence.intelligence.error}</p></div>
      ) : !loadingAI ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          Click "Generate Intelligence" to get AI-powered account analysis and next best action recommendations.
        </div>
      ) : null}
      {aiIntelligence?.generated_at && (
        <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', marginTop: 4 }}>Generated: {fmt(aiIntelligence.generated_at)} — Model: {aiIntelligence.model}</p>
      )}
    </div>
  );
}
